import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { isAuthed, logout as apiLogout, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex items-center border border-border rounded-full overflow-hidden text-xs font-medium" data-testid="lang-toggle">
      <button
        data-testid="lang-it"
        onClick={() => setLang("it")}
        className={`px-3 py-1.5 lift ${lang === "it" ? "bg-primary text-primary-foreground" : "bg-transparent text-foreground hover:bg-accent"}`}
      >IT</button>
      <button
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 lift ${lang === "en" ? "bg-primary text-primary-foreground" : "bg-transparent text-foreground hover:bg-accent"}`}
      >EN</button>
    </div>
  );
}

export default function Navbar({ variant = "marketing" }) {
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const authed = isAuthed();
  const user = authed ? getUser() : null;
  const isGuest = user?.role === "guest";
  const dashPath = isGuest ? "/guest/dashboard" : "/dashboard?tab=calendar";
  const dashLabel = isGuest ? (t("guest.my_bookings") || "Le mie prenotazioni") : t("nav.dashboard");

  const linkCls = "text-sm text-foreground/80 hover:text-primary transition-colors";

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 border-b border-border/60 ${variant === "marketing" ? "backdrop-blur-xl bg-background/70" : "bg-background"}`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group min-w-0">
          <span className="w-8 h-8 shrink-0 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-lg">H</span>
          <span className="font-display text-xl sm:text-2xl tracking-tight leading-none truncate">HospitalityHub</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <a href="/#showcases" className={linkCls} data-testid="nav-showcase">{t("nav.showcase")}</a>
          <Link to="/strutture" className={linkCls} data-testid="nav-directory">{t("nav.directory")}</Link>
          <a href="/#network" className={linkCls} data-testid="nav-network">{t("nav.network")}</a>
          <Link to="/pricing" className={linkCls} data-testid="nav-pricing">{t("nav.pricing") || "Tariffe"}</Link>
          <Link to="/trade" className={linkCls} data-testid="nav-trade">{t("dash.trade_nav")}</Link>
          {isGuest && (
            <Link to="/guest/dashboard#mappe" className={linkCls} data-testid="nav-my-maps">
              {t("guest.my_maps") || "Le mie mappe"}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <LangToggle />
          {authed ? (
            <>
              <NotificationBell/>
              <Button asChild variant="ghost" size="sm" data-testid="nav-dashboard-btn" className="hidden sm:inline-flex">
                <Link to={dashPath}>{dashLabel}</Link>
              </Button>
              <Button
                variant="outline" size="sm" className="lift hidden sm:inline-flex"
                data-testid="nav-logout-btn"
                onClick={async () => { await apiLogout(); nav("/"); }}
              >{t("nav.logout")}</Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="nav-book-btn">
                <Link to="/strutture">{t("nav.book") || (t("nav.directory") ? `${t("nav.directory")} →` : "Prenota")}</Link>
              </Button>
              <div className="relative hidden sm:inline-flex" data-testid="nav-login-menu">
                <details className="group">
                  <summary className="list-none cursor-pointer">
                    <span className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md hover:bg-primary/5 border border-transparent hover:border-border" data-testid="nav-login-btn">
                      {t("nav.login")}
                      <svg width="10" height="6" viewBox="0 0 10 6" className="opacity-70"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                    </span>
                  </summary>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-10" data-testid="nav-login-dropdown">
                    <Link to="/guest/auth?mode=login" className="block px-4 py-3 hover:bg-primary/5 border-b border-border" data-testid="nav-login-guest">
                      <div className="text-sm font-semibold">{t("guest.travellers_area") || "Sei un viaggiatore"}</div>
                      <div className="text-[11px] text-muted-foreground">{t("guest.my_bookings") || "Le mie prenotazioni"}</div>
                    </Link>
                    <Link to="/auth?mode=login" className="block px-4 py-3 hover:bg-primary/5" data-testid="nav-login-host">
                      <div className="text-sm font-semibold">{t("nav.host_area") || "Gestisco una struttura"}</div>
                      <div className="text-[11px] text-muted-foreground">Dashboard host</div>
                    </Link>
                  </div>
                </details>
              </div>
              <Button asChild size="sm" className="lift bg-primary text-primary-foreground hover:bg-primary/90 hidden sm:inline-flex" data-testid="nav-signup-btn">
                <Link to="/auth?mode=register">{t("nav.signup")}</Link>
              </Button>
            </>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border/60 px-6 py-4 flex flex-col gap-3 bg-background">
          <a href="/#showcases" className={linkCls}>{t("nav.showcase")}</a>
          <a href="/#network" className={linkCls}>{t("nav.network")}</a>
          <Link to="/pricing" className={linkCls} data-testid="nav-mobile-pricing">{t("nav.pricing") || "Tariffe"}</Link>
          <Link to="/trade" className={linkCls}>{t("dash.trade_nav")}</Link>
          {isGuest && <Link to="/guest/dashboard#mappe" className={linkCls}>{t("guest.my_maps") || "Le mie mappe"}</Link>}
          <div className="pt-2 border-t border-border/60 flex flex-col gap-2">
            {authed ? (
              <>
                <Button asChild variant="ghost" size="sm"><Link to={dashPath}>{dashLabel}</Link></Button>
                <Button variant="outline" size="sm" onClick={async () => { await apiLogout(); nav("/"); }}>{t("nav.logout")}</Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm"><Link to="/auth?mode=login">{t("nav.login")}</Link></Button>
                <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/auth?mode=register">{t("nav.signup")}</Link></Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
